import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDashboardStats } from '@/hooks/useAdmin';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { StatsCard } from '@/components/admin/StatsCard';
import { RevenueChart } from '@/components/admin/RevenueChart';
import type { StaleOrder } from '@/services/admin';

const STATUS_LABELS: Record<string, string> = {
  paid: 'Paid',
  queued: 'Queued',
  printing: 'Printing',
  post_processing: 'Post-Processing',
  packed: 'Packed',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
  failed: 'Failed',
  refunded: 'Refunded',
  received: 'Received',
  confirmed: 'Confirmed',
};

const STATUS_DOT_COLORS: Record<string, string> = {
  paid: '#3b82f6',
  queued: '#8b5cf6',
  printing: '#f59e0b',
  post_processing: '#f97316',
  packed: '#06b6d4',
  shipped: '#10b981',
  delivered: '#22c55e',
  cancelled: '#ef4444',
  rejected: '#ef4444',
  failed: '#dc2626',
  refunded: '#6b7280',
  received: '#3b82f6',
  confirmed: '#8b5cf6',
};

export const AdminDashboardPage = () => {
  const [days, setDays] = useState(30);
  const { data: stats, isLoading } = useDashboardStats(days);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ color: 'var(--text-tertiary)', padding: 'var(--space-8)' }}>
        Failed to load dashboard data.
      </div>
    );
  }

  const hasAlerts = stats.staleOrders.length > 0 || stats.waitingOnAdminCount > 0;

  return (
    <div>
      {/* Header */}
      <div
        className="flex flex-col sm:flex-row sm:items-center justify-between"
        style={{ marginBottom: 'var(--space-6)', gap: 'var(--space-3)' }}
      >
        <div>
          <h1
            style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-weight-bold)',
              color: 'var(--text-primary)',
              letterSpacing: 'var(--tracking-tight)',
            }}
          >
            Dashboard
          </h1>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)', marginTop: '2px' }}>
            {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          style={{
            padding: 'var(--space-2) var(--space-3)',
            fontSize: 'var(--text-sm)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-default)',
            backgroundColor: 'var(--bg-surface)',
            color: 'var(--text-primary)',
            fontFamily: 'inherit',
          }}
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {/* Action Required Banner */}
      {hasAlerts && (
        <ActionBanner
          staleOrders={stats.staleOrders}
          waitingOnAdmin={stats.waitingOnAdminCount}
        />
      )}

      {/* KPI Strip */}
      <div
        className="grid"
        style={{
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-6)',
        }}
      >
        <StatsCard
          label="Revenue"
          value={`$${stats.totalRevenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          subtitle={`Last ${days} days`}
        />
        {stats.totalRefundAmount > 0 && (
          <StatsCard
            label="Refunded"
            value={`-$${stats.totalRefundAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            accent="danger"
            subtitle={`${stats.totalCancellations} cancelled`}
          />
        )}
        <StatsCard
          label="Orders"
          value={String(stats.totalOrders)}
          subtitle={`Last ${days} days`}
        />
        <StatsCard
          label="Active Queue"
          value={String(stats.activeQueueCount)}
          accent={stats.activeQueueCount > 0 ? 'warning' : 'default'}
        />
        {stats.averageTurnaroundDays !== null && (
          <StatsCard
            label="Avg Turnaround"
            value={`${stats.averageTurnaroundDays}d`}
          />
        )}
        <StatsCard
          label="Quotes"
          value={String(stats.totalQuotes)}
          subtitle={`Last ${days} days`}
        />
        {stats.printFailureCount > 0 && (
          <StatsCard
            label="Failure Rate"
            value={`${stats.failureRate}%`}
            accent="danger"
            subtitle={`${stats.printFailureCount} fail / ${stats.reprintCount} reprint`}
          />
        )}
      </div>

      {/* Two-column: Revenue + Orders by Status */}
      <div
        className="grid"
        style={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
          gap: 'var(--space-4)',
          marginBottom: 'var(--space-4)',
        }}
      >
        {/* Revenue chart */}
        <DashboardCard title="Revenue">
          <RevenueChart data={stats.revenueByDay} />
        </DashboardCard>

        {/* Orders by status */}
        <DashboardCard title="Orders by Status">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {(() => {
              const entries = Object.entries(stats.ordersByStatus)
                .filter(([, count]) => count > 0)
                .sort(([, a], [, b]) => b - a);
              const maxCount = Math.max(...entries.map(([, c]) => c), 1);
              return entries.map(([status, count]) => (
                <StatusRow key={status} status={status} count={count} maxCount={maxCount} />
              ));
            })()}
            {Object.values(stats.ordersByStatus).every((c) => c === 0) && (
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-tertiary)' }}>
                No orders in this period.
              </p>
            )}
          </div>
        </DashboardCard>
      </div>

      {/* Top materials */}
      {stats.topMaterials && Object.keys(stats.topMaterials).length > 0 && (
        <DashboardCard title="Top Materials">
          <div
            className="grid"
            style={{
              gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
              gap: 'var(--space-3)',
            }}
          >
            {Object.entries(stats.topMaterials)
              .sort(([, a], [, b]) => b - a)
              .slice(0, 6)
              .map(([material, count]) => (
                <div
                  key={material}
                  style={{
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--bg-surface-sunken)',
                    textAlign: 'center',
                  }}
                >
                  <p
                    style={{
                      fontSize: 'var(--text-lg)',
                      fontWeight: 'var(--font-weight-bold)',
                      color: 'var(--text-primary)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {count}
                  </p>
                  <p
                    style={{
                      fontSize: 'var(--text-xs)',
                      color: 'var(--text-tertiary)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      fontWeight: 'var(--font-weight-medium)',
                      marginTop: '2px',
                    }}
                  >
                    {material}
                  </p>
                </div>
              ))}
          </div>
        </DashboardCard>
      )}
    </div>
  );
};

// =============================================
// Action banner
// =============================================

const ActionBanner = ({
  staleOrders,
  waitingOnAdmin,
}: {
  staleOrders: readonly StaleOrder[];
  waitingOnAdmin: number;
}) => {
  const totalAlerts = staleOrders.length + (waitingOnAdmin > 0 ? 1 : 0);

  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--admin-amber-border)',
        backgroundColor: 'var(--admin-amber-bg)',
        padding: 'var(--space-4) var(--space-5)',
        marginBottom: 'var(--space-6)',
      }}
    >
      <div
        className="flex items-center justify-between"
        style={{ marginBottom: staleOrders.length > 0 ? 'var(--space-3)' : 0 }}
      >
        <p
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--admin-amber-text)',
          }}
        >
          {totalAlerts} item{totalAlerts > 1 ? 's' : ''} need{totalAlerts === 1 ? 's' : ''} attention
        </p>
        {waitingOnAdmin > 0 && (
          <Link
            to="/admin/orders"
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--admin-amber-text)',
              textDecoration: 'underline',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            {waitingOnAdmin} awaiting admin action
          </Link>
        )}
      </div>

      {staleOrders.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {staleOrders.slice(0, 5).map((so) => (
            <div
              key={so.orderId}
              className="flex items-center justify-between"
              style={{ fontSize: 'var(--text-sm)' }}
            >
              <Link
                to={`/admin/orders/${so.orderId}`}
                style={{
                  color: 'var(--admin-amber-text)',
                  textDecoration: 'none',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-weight-medium)',
                }}
              >
                #{so.orderId.slice(-8).toUpperCase()}
              </Link>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--admin-amber-text)', opacity: 0.8 }}>
                {so.status.replace(/_/g, ' ')} — {so.staleDays}d stale
              </span>
            </div>
          ))}
          {staleOrders.length > 5 && (
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--admin-amber-text)', opacity: 0.7 }}>
              +{staleOrders.length - 5} more
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================
// Dashboard card wrapper
// =============================================

const DashboardCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div
    style={{
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--border-default)',
      backgroundColor: 'var(--bg-surface)',
      padding: 'var(--space-5)',
    }}
  >
    <h2
      style={{
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--text-secondary)',
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
        marginBottom: 'var(--space-4)',
      }}
    >
      {title}
    </h2>
    {children}
  </div>
);

// =============================================
// Status row with bar visualization
// =============================================

const StatusRow = ({ status, count, maxCount }: { status: string; count: number; maxCount: number }) => {
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  const dotColor = STATUS_DOT_COLORS[status] ?? 'var(--color-neutral-400)';

  return (
    <div>
      <div className="flex items-center" style={{ gap: 'var(--space-3)', marginBottom: '3px' }}>
        <span
          style={{
            width: '8px',
            height: '8px',
            borderRadius: 'var(--radius-full)',
            backgroundColor: dotColor,
            flexShrink: 0,
          }}
        />
        <span
          style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--text-secondary)',
            flex: 1,
            textTransform: 'capitalize',
          }}
        >
          {STATUS_LABELS[status] ?? status}
        </span>
        <span
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--text-primary)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          {count}
        </span>
      </div>
      <div
        style={{
          height: '3px',
          borderRadius: '2px',
          backgroundColor: 'var(--bg-surface-sunken)',
          marginLeft: 'calc(8px + var(--space-3))',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            borderRadius: '2px',
            backgroundColor: dotColor,
            opacity: 0.6,
            transition: 'width var(--duration-normal) var(--ease-out)',
          }}
        />
      </div>
    </div>
  );
};
